import React, { Component } from 'react';
import './hr-template.css';
import NavBar from './NavBar';
import hr2 from './assets/hrmodule/hr2-org-chart.webp';
import hr3 from './assets/hrmodule/hr3-surveys.webp';
import hr4 from './assets/hrmodule/hr4-survey-results.webp';
import hr5 from './assets/hrmodule/hr5-back-office.webp';
import hr6 from './assets/hrmodule/hr6-recruitment.webp';
import hr7 from './assets/hrmodule/hr7-employee-records.webp';

interface HRTemplateProps {}

interface HRTemplateState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  lightboxSrc: string | null;
}

class HRTemplate extends Component<HRTemplateProps, HRTemplateState> {
  constructor(props: HRTemplateProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      lightboxSrc: null
    };
  }

  openLightbox = (src: string) => {
    this.setState({ lightboxSrc: src });
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null });
  };

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  closeAllMenus = () => {
    this.setState({ isMenuOpen: false, isProductsMenuOpen: false, isSolutionsMenuOpen: false });
  };

  render() {
    return (
      <div className="hr-template-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="hr-template-hero-section">
          <div className="hr-template-hero-video-background">
            <video
              preload="metadata"
              autoPlay
              loop
              muted
              playsInline
              className="hr-template-hero-video"
            >
              <source src={require('./assets/video/web/19693287-hd_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="hr-template-hero-content">
            <div className="hr-template-hero-text">
              <span className="hr-template-hero-eyebrow">pEntities-HR Module</span>
              <h1 className="hr-template-hero-title">
                Transform HR Operations with Digital Intelligence
              </h1>
              <p className="hr-template-hero-subtitle">
                A modern, mobile-first employee destination built natively on Microsoft 365, running inside
                Microsoft Teams, on the tenant you already own.
              </p>
              <div className="hr-template-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hr-template-hero-cta-btn"
                >
                  Schedule a Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description Section */}
        <section id="overview" className="hr-gendesc-section">
          <div className="hr-template-container">
            <div className="hr-gendesc-content">
              <span className="hr-gendesc-label">General Description</span>
              <h2 className="hr-gendesc-title">One employee destination. Every brand, every country, every device.</h2>
              <p>
                A single, trusted home for internal news, knowledge and everyday services reaching every employee
                across brands and countries, and ending the reliance on all-staff email.
              </p>
              <p>
                <b>Two apps, one platform.</b> An <b>Employee App</b> on phone, web and Microsoft Teams for the
                people who use it, and an <b>HR back office</b> where communications are written, translated,
                targeted, approved and published.
              </p>
              <p>
                <b>Your tenant, your data.</b> Everything runs inside your own Microsoft environment, so employee
                data never leaves your control EU residency and GDPR by design, with the same sign-in your people
                already use.
              </p>
              <p>
                <b>Live today, not a specification.</b> Every screenshot on this page is taken from the working
                product inside Microsoft Teams.
              </p>
            </div>
          </div>
        </section>

        {/* Employee Experience dark */}
        <section id="employee-experience" className="hr-dark-section">
          <div className="hr-template-container">
            <span className="hr-dark-eyebrow">The Employee Experience</span>
            <h2 className="hr-dark-title">Everything an employee needs, in the app they already use all day</h2>
            <p className="hr-dark-lead">
              No portal to log into, no email to dig through. One personal home for news, knowledge and the
              everyday services people actually need on any device and inside Teams.
            </p>

            <div className="hr-dark-block">
              <h3>One app, on every device they already carry</h3>
              <p>
                The same employee app on iPhone, Android, the web and inside Microsoft Teams, opened with the
                sign-in people already use. No new password, no portal to remember.
              </p>
              <ul>
                <li><b>A home page built for the individual</b> shaped by their company, brand, role and location.</li>
                <li><b>Announcements that cannot be missed</b> pinned at the top, colour-coded, with read tracking.</li>
                <li><b>Services, not just reading</b> leave balance, tasks, tickets and team information one tap away.</li>
                <li><b>Everything else in one place</b> shortcuts to the other systems people need through the day.</li>
              </ul>
            </div>

            <div className="hr-dark-block">
              <h3>Communications that reach everyone in their own language</h3>
              <p>
                Write once and the message is translated automatically, so every employee reads it in the language
                they prefer. HR can review, correct and lock any translation, and a locked version is never
                overwritten.
              </p>
              <ul>
                <li><b>Targeted and scheduled</b> to a person, a department, a brand or the whole group, at the right local time.</li>
                <li><b>Proof that it landed</b> read receipts, unread badges and employee ratings show what actually resonated.</li>
                <li><b>Knowledge that stays current</b> articles and documents follow a draft, review and publish cycle, with version history and dates that retire content automatically.</li>
                <li><b>Everyone sees only what is theirs</b> targeting and permissions are applied everywhere, including search.</li>
              </ul>
            </div>

            <div className="hr-dark-block">
              <h3>Sensitive material stays where it belongs</h3>
              <p>
                Internal communications carry real risk when they travel. Editorial content cannot be downloaded or
                printed, sensitive material carries a personal watermark, screen capture is blocked or obscured on
                mobile, and media is served in a form that leaves no copy behind on the device.
              </p>
            </div>
          </div>
        </section>

        {/* HR Control & Insight white */}
        <section id="hr-console" className="hr-light-section">
          <div className="hr-template-container">
            <span className="hr-light-eyebrow">HR Control &amp; Insight</span>
            <h2 className="hr-light-title">One console for people, communications and the numbers</h2>
            <p className="hr-light-lead">
              HR runs everything from a single back office with a directory that keeps itself up to date and
              dashboards that show the investment is working.
            </p>

            <div className="hr-light-block">
              <h3>A directory and org chart that maintain themselves</h3>
              <figure className="hr-figure is-float" onClick={() => this.openLightbox(hr2)}>
                <img loading="lazy" decoding="async" src={hr2} alt="The organizational chart assembled automatically from Microsoft Entra ID" />
                <figcaption>The org chart, assembled automatically, with live presence and instant search nothing to maintain by hand.</figcaption>
              </figure>
              <p>
                The people directory and the organisation chart build themselves from the company's own records, so
                they are accurate on the day someone joins, moves or leaves. Nobody has to redraw anything.
              </p>
              <p>
                Employees can see who is available, and start a chat, a call or a meeting from the same screen
                which is usually the first thing they use the app for.
              </p>
            </div>

            <div className="hr-light-block">
              <h3>Ask the workforce, and see the answer immediately</h3>
              <figure className="hr-figure is-float is-left" onClick={() => this.openLightbox(hr3)}>
                <img loading="lazy" decoding="async" src={hr3} alt="HR creates and manages campaigns and surveys" />
                <figcaption>Campaigns and surveys, targeted to any audience, with live response tracking.</figcaption>
              </figure>
              <p>
                Reactions and ratings on every communication turn a one-way intranet into a conversation. Beyond
                that, HR can run polls and full surveys anonymous where honesty matters targeted at any part of
                the organisation.
              </p>
              <figure className="hr-figure is-float" onClick={() => this.openLightbox(hr4)}>
                <img loading="lazy" decoding="async" src={hr4} alt="Survey results aggregate automatically" />
                <figcaption>Results aggregate as they arrive response rates, average ratings and a breakdown per question.</figcaption>
              </figure>
              <p>
                Results aggregate as they arrive, so engagement is a number you can quote to the board rather than
                an impression, and a poll asked on Monday is answered by Tuesday.
              </p>
            </div>

            <div className="hr-light-block">
              <h3>One back office, and the numbers to prove it works</h3>
              <figure className="hr-figure is-float is-left" onClick={() => this.openLightbox(hr5)}>
                <img loading="lazy" decoding="async" src={hr5} alt="The HR back office employee record" />
                <figcaption>The HR back office a complete record, with directory, documents, tasks and role-based administration.</figcaption>
              </figure>
              <p>
                Writing, translating, targeting, approving, publishing and reporting all happen in one place, with
                access given by role: who may write, who may approve, who may publish to a whole country. Every
                action is recorded, so there is always an answer to who changed what and when.
              </p>
              <p>
                Dashboards then give the group, each country and each company the measures that matter adoption,
                engagement, how well people find what they need, whether content is current and each audience sees
                only its own figures.
              </p>
              <p>
                It is built on Microsoft 365 and runs on infrastructure you own, so availability, backup, recovery
                and the data itself stay yours.
              </p>
            </div>
          </div>
        </section>

        {/* Beyond the brief modules */}
        <section id="beyond" className="hr-modules-section">
          <div className="hr-template-container">
            <span className="hr-modules-eyebrow">Beyond the brief, included</span>
            <h2 className="hr-modules-title">A complete employee platform already built, at no extra licence cost</h2>
            <p className="hr-modules-lead">
              You're not choosing between an intranet and a wider employee platform. It's both same foundation,
              same tenant, no new vendor, no new login, no new security review.
            </p>

            <div className="hr-modules-grid">
              <div className="hr-module-card">
                <h3>Leave &amp; absence</h3>
                <p>Self-service requests, working days and public holidays calculated automatically, multi-step approval and a complete history.</p>
              </div>
              <div className="hr-module-card">
                <h3>Onboarding &amp; offboarding</h3>
                <p>A checklist for every joiner and leaver, with owners, dates and live progress and the whole process automated.</p>
              </div>
              <div className="hr-module-card">
                <h3>Recruitment</h3>
                <p>Post roles, publish a careers page, collect CVs, run a visual pipeline and score candidates objectively.</p>
              </div>
              <div className="hr-module-card">
                <h3>Training &amp; compliance</h3>
                <p>Records per employee, expiry and overdue tracking, manager alerts before the date and policy acknowledgements on file.</p>
              </div>
              <div className="hr-module-card">
                <h3>Process automation</h3>
                <p>Build HR processes visually onboarding, document filing, expiry reminders without writing code or involving IT.</p>
              </div>
              <div className="hr-module-card">
                <h3>AI assistant &amp; reporting</h3>
                <p>Ask questions in plain language across employee data and content, and get the answer without building a report.</p>
              </div>
              <div className="hr-module-card">
                <h3>HR &amp; Finance alignment</h3>
                <p>Leave costs, onboarding expenses and payroll captured automatically, with pay-equity insight.</p>
              </div>
              <div className="hr-module-card">
                <h3>Populated in one click</h3>
                <p>The platform stands up fully populated from your existing directory, and re-syncs safely afterwards.</p>
              </div>
            </div>

            <div className="hr-figure-pair">
              <figure className="hr-figure" onClick={() => this.openLightbox(hr6)}>
                <img loading="lazy" decoding="async" src={hr6} alt="The built-in recruitment ATS" />
                <figcaption>The built-in recruitment tool a visual candidate pipeline with CV preview and objective scoring, at no extra cost.</figcaption>
              </figure>
              <figure className="hr-figure" onClick={() => this.openLightbox(hr7)}>
                <img loading="lazy" decoding="async" src={hr7} alt="The employee records directory" />
                <figcaption>The employee records that power the directory, the personalised app and every report.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="hr-template-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">Purpose-built modules for the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              One platform, four specialised modules each with its own domain AI agent, all running inside
              Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        {/* Image Lightbox */}
        {this.state.lightboxSrc && (
          <div className="hr-lightbox" onClick={this.closeLightbox} role="dialog" aria-modal="true">
            <button className="hr-lightbox-close" onClick={this.closeLightbox} aria-label="Close">✕</button>
            <img loading="lazy" decoding="async"
              className="hr-lightbox-img"
              src={this.state.lightboxSrc}
              alt="Enlarged screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default HRTemplate;

